var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button, Menu, useToast } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { useHistory } from 'react-router-dom';
import { DuplicateDocumentDialog, RenameDocumentDialog } from 'app/domains/documents';
import { DeleteDocumentDialog, DownloadDocumentDialog, ROOT, useACLCan } from 'app/domains/filesystem';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { documentFlush } from 'shared/domains/documents';
import { DocumentActionsDropdownDocument } from './DocumentActionsDropdown.gql';
import { ImportDocumentButton } from './ImportDocumentButton';
export function DocumentActionsDropdown(props) {
    const { documentId } = props;
    const { addToast } = useToast();
    const history = useHistory();
    const [renderDeleteDialog, setShowDeleteDialog] = useState(false);
    const [renderRenameDialog, setShowRenameDialog] = useState(false);
    const [renderDownloadDialog, setShowDownloadDialog] = useState(false);
    const [renderDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const { data, loading, error: queryError, } = useQuery(DocumentActionsDropdownDocument, {
        variables: { id: documentId },
    });
    const { folderId, type, acls, teamAcls } = (data === null || data === void 0 ? void 0 : data.fileSystemDocument) || {};
    const { accessLevel } = useACLCan({ userAcls: acls, teamAcls });
    const restricted = accessLevel !== 'full' || loading || Boolean(queryError);
    const isRtcAvailable = type === DocumentType.Composed || type === DocumentType.Freetext;
    const showDeleteDialog = () => setShowDeleteDialog(true);
    const hideDeleteDialog = () => setShowDeleteDialog(false);
    const showRenameDialog = () => setShowRenameDialog(true);
    const hideRenameDialog = () => setShowRenameDialog(false);
    const showDownloadDialog = () => setShowDownloadDialog(true);
    const hideDownloadDialog = () => setShowDownloadDialog(false);
    const showDuplicateDialog = () => setShowDuplicateDialog(true);
    const hideDuplicateDialog = () => setShowDuplicateDialog(false);
    const onDeleted = () => {
        if (isRtcAvailable) {
            documentFlush({ state: 'initialized' });
        }
        if (folderId && folderId !== ROOT) {
            return history.push(`/files/${folderId}`);
        }
        return history.push(`/files`);
    };
    const onDuplicated = (draftId) => {
        history.push(`/draft/${draftId}`);
    };
    const handleDuplicate = () => __awaiter(this, void 0, void 0, function* () {
        try {
            if (!documentId) {
                throw Error();
            }
            showDuplicateDialog();
        }
        catch (error) {
            addToast({ title: t `Could not create copy.`, appearance: 'danger' });
        }
    });
    return (<Fragment>
      <Menu.Root target={<Button disabled={loading} aria-label={t `More options`} appearance="secondary" size="xs">
            <Falcon icon="ellipsis-v"/>
            <Trans comment="Label for document actions dropdown">Actions</Trans>
          </Button>}>
        <Menu.Item onClick={showRenameDialog} disabled={restricted}>
          <Falcon icon="pen-to-square"/>
          <Menu.ItemText primary={t({ message: 'Edit name', comment: 'Dropdown label for renaming document' })}/>
        </Menu.Item>

        <Menu.Item onClick={showDownloadDialog} disabled={restricted}>
          <Falcon icon="download"/>
          <Menu.ItemText primary={t({ message: 'Download', comment: 'Dropdown label for downloading document' })}/>
        </Menu.Item>

        <Menu.Item onClick={handleDuplicate} disabled={restricted}>
          <Falcon icon="copy"/>
          <Menu.ItemText primary={t({ message: 'Copy', comment: 'Dropdown label for copying document' })}/>
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item onClick={showDeleteDialog} disabled={restricted}>
          <Falcon icon="trash-can"/>
          <Menu.ItemText primary={t({ message: 'Delete', comment: 'Dropdown label for deleting document' })}/>
        </Menu.Item>
      </Menu.Root>

      <ImportDocumentButton disabled={restricted}/>

      {renderDeleteDialog && <DeleteDocumentDialog id={documentId} onDeleted={onDeleted} onClose={hideDeleteDialog}/>}

      {renderRenameDialog && <RenameDocumentDialog onClose={hideRenameDialog}/>}

      {renderDownloadDialog && <DownloadDocumentDialog id={documentId} onClose={hideDownloadDialog}/>}

      {renderDuplicateDialog && (<DuplicateDocumentDialog id={documentId} onDuplicated={onDuplicated} onClose={hideDuplicateDialog}/>)}
    </Fragment>);
}
